import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Login + activation (008 US1, 006/009). On login the JWT yields role/tenant;
 * desktop additionally binds the device via fingerprint, activating against the
 * customer's tenant license (resolved server-side — no client-held key). Sign-in
 * is the only entry.
 */
import { useEffect, useState } from "react";
import { decodeAccessToken } from "@genz/platform";
import { useServices } from "./context.js";
import { activationErrorMessage } from "../activation/errors.js";
import { useSessionStore } from "../stores/session.js";
import { noteSignIn, useReauthPrompt, useReauthStore } from "./reauth.js";
import { warmSessionCache } from "../stores/sessionWarmup.js";
import { useAppearance, DEFAULT_APPEARANCE } from "../stores/appearance.js";

/** Secret-store key for the desktop "Remember me" entry — the EMAIL only, device-encrypted
 *  at rest. (It held the password until 2026-08-04; see the note in the component.) */
const REMEMBER_SECRET_KEY = "genz.login.credential";

/**
 * A COARSE, human name for this machine, for Settings → Signed-in devices (WS-Q Q-5).
 *
 * The point of a label is that its owner recognises the row and can end it. So it is an OS
 * family and nothing else: no fingerprint, no IP, no serial, no screen metrics — a value the
 * browser already announces on every request, shown back only to the account that made it.
 *
 * The OS HOSTNAME would read better ("Studio iMac"), but it is not reachable from the webview
 * without a new native command and this wave adds no Rust. Filed as an owner item; until then
 * the operator can rename nothing and this is the honest label.
 */
function describeThisDevice(kind: string): string {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const os = /Mac|iPhone|iPad/i.test(ua) ? "Mac"
    : /Windows/i.test(ua) ? "Windows"
    : /Android/i.test(ua) ? "Android"
    : /Linux|X11/i.test(ua) ? "Linux"
    : "Unknown";
  return kind === "desktop" ? `${os} desktop app` : `${os} browser`;
}

export function Login() {
  const { client, io, engine, logger, purgeAccountBuffers } = useServices();
  const setSession = useSessionStore((s) => s.setSession);
  // WS-Q / LB-12: after an involuntary expiry this form is a RE-AUTHENTICATE prompt, not a
  // fresh sign-in — the workspace is still on this device and the operator is the same person.
  const reauth = useReauthPrompt();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Desktop "Remember me": macOS/Windows AutoFill does NOT reach a Tauri webview (that's a
  // Safari/system-browser feature), so the app provides its own — EMAIL ONLY, in the
  // device-encrypted secret store, never in localStorage.
  //
  // It deliberately does NOT store the password. Staying signed in is the REFRESH TOKEN's
  // job (session.ts persists it to the same secret store and restores it at boot), so a
  // stored password bought nothing for those 30 days — while handing the account to anyone
  // who opened the app on a shared machine, because the form prefilled both fields. After
  // the hard 30-day expiry no token can sign you back in (that is the point of the expiry),
  // so the most a "remember" can honestly do is fill the email and let you type one secret.
  const [remember, setRemember] = useState(true);
  // WS-Q / D-35 "Keep me signed in on this device" — a SECOND, separate control. "Remember my
  // email" fills a field; this one asks the cloud for a 180-day sliding chain (capped at 365
  // days from first login) instead of the hard 30-day one, and what gets kept is a revocable
  // refresh token in this device's encrypted store — never the password.
  //
  // On by default on the desktop, which is a machine the operator owns and can end from
  // Settings → Signed-in devices. NEVER offered on web: a browser profile is not a trusted
  // device, and web must not get the long chain at all.
  const [keepSignedIn, setKeepSignedIn] = useState(io.kind === "desktop");
  const appearance = useAppearance();
  useEffect(() => {
    if (io.kind !== "desktop" || !io.secretGet) return;
    let alive = true;
    void io.secretGet(REMEMBER_SECRET_KEY).then((raw) => {
      if (!alive || !raw) return;
      try {
        const cred = JSON.parse(raw) as { email?: string; password?: string };
        if (cred.email) setEmail(cred.email);
        setRemember(true);
        // MIGRATION: installs from before this change hold a plaintext password here.
        // Rewrite the entry without it so the credential stops living on disk — the
        // password field is never read back into the form again.
        if (cred.password !== undefined && io.secretSet) {
          void io.secretSet(REMEMBER_SECRET_KEY, JSON.stringify({ email: cred.email ?? "" }))
            .catch(() => { /* best-effort cleanup */ });
        }
      } catch { /* corrupt entry → ignore */ }
    }).catch(() => { /* secret store unavailable → plain login */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-authenticate mode prefills the email of the session that just expired — the same
  // operator, the same account, one password field. It wins over the remembered email above
  // because it is the identity that was actually signed in a moment ago.
  useEffect(() => {
    if (reauth.pending && reauth.email) setEmail(reauth.email);
  }, [reauth.pending, reauth.email]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { tokens, role } = await client.login(email, password, {
        // Web never asks for the long chain, whatever this device's state says.
        persistent: io.kind === "desktop" && keepSignedIn,
        deviceLabel: describeThisDevice(io.kind),
      });
      // Persist or clear the remembered credential per the checkbox — only after a
      // SUCCESSFUL login (never store a typo), and only on desktop (web no-ops).
      if (io.kind === "desktop" && io.secretSet && io.secretDelete) {
        void (remember
          // EMAIL ONLY — never the password (see the note above). The refresh token that
          // actually keeps this device signed in is persisted separately by session.ts.
          ? io.secretSet(REMEMBER_SECRET_KEY, JSON.stringify({ email }))
          : io.secretDelete(REMEMBER_SECRET_KEY)
        ).catch(() => { /* best-effort — login itself already succeeded */ });
      }
      // Real tenant/customer ids come from the JWT access-token claims (006). The
      // server remains the only authority (it re-checks every request); decoding
      // here is purely to show a REAL identity in the UI. Falls back to "self"
      // only if a token is somehow unparseable. A global super_admin has a null
      // tenant — surface it as "global" for display.
      const claims = decodeAccessToken(tokens.access_token);
      // WS-Q: if a DIFFERENT customer last used this device, drop their content BEFORE the
      // new session is installed — the purge clears the session store, so doing it after
      // would sign the arriving account straight back out. Same customer ⇒ nothing happens,
      // which is the whole point: re-signing in must not cost a 1.5 GB re-download.
      await noteSignIn(claims?.customer ?? "self", {
        io, engine,
        log: (level, message, ctx) => logger?.log(level, message, ctx),
        // H-2: the COMPOSED purge (telemetry + client log). `noteSignIn` only purges when the
        // arriving customer DIFFERS, which is exactly when the previous account's telemetry
        // buffer must not survive into this session.
        purgeBuffers: () => purgeAccountBuffers(),
      });
      useReauthStore.getState().dismiss();
      setSession({
        customerId: claims?.customer ?? "self",
        tenantId: claims?.tenant ?? (claims ? "global" : "self"),
        role: claims?.role ?? role,
        email,
        tokens,
      });
      // Desktop: bind this device (009) against the tenant license (server-resolved).
      // Web skips device activation. A genuine activation failure is surfaced (a
      // tenant with no license can still sign in — generation will report the gate).
      if (io.kind === "desktop") {
        const fp = await io.deviceFingerprint();
        try {
          const activation = await client.activate(fp, "this device");
          useSessionStore.getState().setActivation(activation);
        } catch (e) {
          // `HttpError.message` is the REQUEST LINE ("POST /api/activation/activate -> 403"),
          // so the commonest refusal — the tenant's device cap — reached the operator as a
          // bare status code with no reason and no way out. Translate the cloud's reason.
          setError(`Signed in, but this device could not be activated. ${activationErrorMessage(e)}`);
        }
      }
      try {
        const ent = await client.entitlements();
        useSessionStore.getState().setEntitlements(ent);
      } catch (e) {
        setError(`Signed in, but entitlements could not be loaded: ${e instanceof Error ? e.message : String(e)}`);
      }
      // Warm the per-client cache once (settings, template-options + reference images)
      // so every screen opens instantly afterwards — fire-and-forget, never blocks login.
      void warmSessionCache(client);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  // Only a prompt that KNOWS whose session expired can prefill and lock the email. Without an
  // email it is an ordinary sign-in with an ordinary form, which is the honest presentation.
  const reauthMode = reauth.pending && !!reauth.email;
  // The two causes are not the same event and must not wear the same sentence: an expiry
  // happened on the SERVER, while an unreadable device store means the saved sign-in on THIS
  // machine could not be read — possibly while the session is still perfectly alive. Saying
  // "expired" there sends the operator looking at the wrong thing, and a machine where it
  // repeats is a device-store bug report, not a session-length one (WS-Q review-1 I-2).
  const unreadableStore = reauthMode && reauth.reason === "unreadable-store";

  const brandMark = appearance.logo_url ? (
    <img className="brand-logo brand-logo--lg" src={appearance.logo_url} alt="" aria-hidden="true" />
  ) : appearance.short_mark === DEFAULT_APPEARANCE.short_mark ? (
    <img className="brand-logo brand-logo--lg brand-logo--mark" src="/brand-mark.svg" alt="" aria-hidden="true" />
  ) : (
    <span className="brand-mark login__mark" aria-hidden="true">{appearance.short_mark}</span>
  );

  // WS-Z7: ONE centred panel. The two-column identity/card layout was the product's last
  // marketing shape — a display heading and a tagline beside the only form on the screen,
  // over a canvas carrying two radial volt washes (R13). The mark, the workspace name and
  // the tagline all moved into the panel's own head, at the panel's own scale, and every
  // control, handle and promise came with them.
  //
  // ONE line did NOT: the `.login-hero__meta` strap line, "Author the template once. Create
  // mock-ups of the catalog forever." It was dropped, not moved, and it is nowhere in the
  // tree. It could go because the tagline directly above it already says what the product
  // is — two sentences saying the same thing is the redundancy rule (A) is about, and the
  // strap line carried no control, no handle and no capability with it.
  return (
    <div className="login-wrap">
      <form className="login" onSubmit={submit} data-testid="login" aria-label={`Sign in to ${appearance.app_name}`}>
        <div className="login__brand">
          {brandMark}
          <h1 className="login__app">{appearance.app_name}</h1>
          <p className="login__tagline">{appearance.tagline}</p>
        </div>

        <div className="login__head" {...(reauthMode ? { "data-testid": "reauth-prompt" } : {})}>
          <h2 className="login__title">
            {unreadableStore ? "Sign in again on this device" : reauthMode ? "Session expired" : "Welcome back"}
          </h2>
          <p className="login__sub">
            {unreadableStore
              ? "The saved sign-in on this device could not be read, so this launch starts signed out. Sign in again to keep working — your templates and mock-ups are still on this device."
              : reauthMode
                ? "Your session expired. Sign in again to keep working — your templates and mock-ups are still on this device."
                : "Sign in to your workspace."}
          </p>
        </div>

        {/* Rule 9: two tones on the page body. A failed sign-in says WHICH it is rather
            than relying on `.banner`'s default happening to be the danger one. */}
        {error && (
          <p className="banner banner--danger" role="alert">
            <ErrorNotice detail={error} />
          </p>
        )}

        <div className="field">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@studio.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            // Re-authenticating restores THIS session; signing in as someone else is a
            // different act, and it is one click away ("Sign in as someone else", below).
            readOnly={reauthMode}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {io.kind === "desktop" && (
          <label className="login__remember" data-testid="login-remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />{" "}
            Remember my email on this device
          </label>
        )}

        {io.kind === "desktop" && (
          <label className="login__remember" data-testid="login-keep-signed-in">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedIn(e.target.checked)}
            />{" "}
            Keep me signed in on this device
            {/* Rule 10 retires the `hint` CLASS, not this sentence. It is a SECURITY
                PROMISE — "never your password" — and a promise behind a hover is a promise
                most operators never read. It stays on screen, in the caption voice. */}
            <span className="caption login__promise">
              Stores a long-lived sign-in token in this computer&rsquo;s encrypted store.
              Never your password. End it any time from Settings &rarr; Signed-in devices.
            </span>
          </label>
        )}

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? "Signing in…" : reauthMode ? "Sign in again" : "Sign in"}
        </button>
        {reauthMode && (
          // The way OUT of re-authenticate mode. Without it a locked email field is a trap
          // for the one operator who genuinely does want to hand the machine to someone else.
          <button
            type="button"
            className="btn btn--ghost btn--block"
            data-testid="reauth-other-account"
            onClick={() => useReauthStore.getState().dismiss()}
          >
            Sign in as someone else
          </button>
        )}
        {/* Not a hint: it explains no control. It is information, so it is the caption
            voice — where rule 9 puts information that is not a danger or a warning. */}
        <p className="caption login__support" data-testid="login-support">
          Trouble signing in? Contact your workspace admin.
        </p>
      </form>
    </div>
  );
}
